import {
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import { AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { Select } from "@ofqual/portal-components/components/Select";
import { Checkbox } from "@ofqual/portal-components/components/Checkbox";
import { TextField } from "@ofqual/portal-components/components/TextField";

import { Controller, useForm } from "react-hook-form";

import {
  NonNullableOrganisationState,
  NonNullableRecognitionApplicationState,
} from "../../../redux/state";
import { ApplicationAppBar } from "../ApplicationAppBar";
import { ReviewerSectionAndDialog } from "../dialogs/ReviewerSectionAndDialog";
import { applicationFieldLabels } from "../../fieldLabels";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { nullOptionInjector } from "@ofqual/portal-core/core/utils/nullOptionInjector";
import {
  applicationPanelDecisionService,
  applicationReviewMeetingOutcomeService,
  applicationReviewOutcomeService,
  orgsService,
} from "../../../services";
import { ReviewDocuments } from "./reviewDocuments/ReviewDocuments";
import {
  RecognitionApplicationViewModel,
  fromViewModel,
  toViewModel,
} from "../../../domain/viewModels/RecognitionApplicationViewModel";
import {
  buildMetaData,
  getLookupTracker,
  modelToJsonPatchDocument,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { RecognitionApplication } from "../../../domain/RecognitionApplication";
import { useDispatch, useSelector } from "react-redux";
import { editApplication } from "../../../redux/actions";
import { useCallback, useMemo } from "react";
import { authSelector } from "../../../redux/selectors";
import { useResetOnModelUpdate } from "@ofqual/portal-core/core/hooks";

const useStyles = makeStyles((theme) => ({
  formHeading: {
    color: "#9f9f9f",
    fontSize: "1em",
    marginBottom: "30px",
    fontWeight: 600,
    position: "relative",
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

type ReviewProps = {
  orgState: NonNullableOrganisationState;
  applicationState: NonNullableRecognitionApplicationState;
  canEdit: boolean;
};

export const Review: React.FC<ReviewProps> = ({
  orgState,
  applicationState,
  canEdit,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  let { application } = applicationState;

  const viewModel = useMemo(() => toViewModel(application), [application]);

  const {
    control,
    handleSubmit,
    formState,
    reset,
  } = useForm<RecognitionApplicationViewModel>({
    defaultValues: viewModel,
    shouldUnregister: false,
  });

  useResetOnModelUpdate(viewModel, reset);

  const { dirtyFields } = formState;

  const lookupTracker = useMemo(() => getLookupTracker(), []);
  const onSubmit = (viewModel: RecognitionApplicationViewModel) => {
    const {
      application: newApplication,
      newReviewAttachments,
      newPanelAttachments,
      deletedReviewAttachmentIds,
      deletedPanelAttachmentIds,
    } = fromViewModel(viewModel);

    const fields = Object.keys(dirtyFields) as (keyof RecognitionApplication)[];

    const fieldsToIgnore: (keyof RecognitionApplicationViewModel)[] = [
      "reviewAttachments",
      "panelAttachments",
      "newReviewAttachments",
      "newPanelAttachments",
    ];

    const cleanFields = fields.filter((item) => !fieldsToIgnore.includes(item));

    const document = modelToJsonPatchDocument(newApplication, cleanFields);
    const meta = buildMetaData(
      newApplication,
      application,
      cleanFields,
      lookupTracker
    );

    dispatch(
      editApplication(
        orgState.org.id,
        application.id,
        { document, meta },
        newReviewAttachments,
        newPanelAttachments,
        deletedReviewAttachmentIds,
        deletedPanelAttachmentIds
      )
    );
  };

  const [
    panelDecisionLookups,
    reviewMeetingOutcomeLookups,
    reviewOutcomeLookups,
  ] = useLookup(
    nullOptionInjector(applicationPanelDecisionService),
    nullOptionInjector(applicationReviewMeetingOutcomeService),
    nullOptionInjector(applicationReviewOutcomeService)
  );

  const { token } = useSelector(authSelector);
  const downloadReviewBlob = useCallback(
    (blobId: string) =>
      orgsService.downloadApplicationReviewAttachment(
        orgState.org.id,
        application.id,
        blobId,
        { token }
      ),
    [application.id, orgState.org.id, token]
  );

  const downloadPanelBlob = useCallback(
    (blobId: string) =>
      orgsService.downloadApplicationPanelAttachment(
        orgState.org.id,
        application.id,
        blobId,
        { token }
      ),
    [application.id, orgState.org.id, token]
  );

  return (
    <>
      <ApplicationAppBar
        orgState={orgState}
        applicationState={applicationState}
        tabName="Review"
        canEdit={canEdit}
      >
        {canEdit ? (
          <AppBarSaveButton
            onClick={handleSubmit(onSubmit)}
            isSubmitting={applicationState.status === "UPDATING"}
            isSubmitted={applicationState.lastCrudResult.isSuccess}
            dataTest="save-button"
            disabled={false}
          />
        ) : null}
      </ApplicationAppBar>

      <Typography variant="h6">Application Review</Typography>

      <Paper className={classes.formSection}>
        <Grid container spacing={3}>
          <Grid item xs={12} xl={4} md={6}>
            <ReviewerSectionAndDialog
              upnFieldName="c1bReviewerUPN"
              displayNameFieldName="c1bReviewerDisplayName"
              label="C.1(b) Reviewer"
              dataTest="c1b-reviewer-upn"
              control={control}
              readOnly={!canEdit}
            />
          </Grid>
          <Grid item xs={12} xl={4} md={6}>
            <ReviewerSectionAndDialog
              upnFieldName="dReviewerUPN"
              displayNameFieldName="dReviewerDisplayName"
              label="D Reviewer"
              dataTest="d-reviewer-upn"
              control={control}
              readOnly={!canEdit}
            />
          </Grid>
          <Grid item xs={12} xl={4} md={6}>
            <ReviewerSectionAndDialog
              upnFieldName="mainReviewerUPN"
              displayNameFieldName="mainReviewerDisplayName"
              label="Main Reviewer"
              dataTest="main-reviewer-upn"
              control={control}
              readOnly={!canEdit}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} xl={6} lg={6}>
            <Controller
              control={control}
              name="c1bReviewCompletedDate"
              as={
                <DatePicker
                  label={applicationFieldLabels.c1bReviewCompletedDate}
                  required
                  dataTest="c1b-review-completed-date"
                  readOnly={!canEdit}
                />
              }
            />
          </Grid>
          <Grid item xs={12} xl={6} lg={6}>
            <Controller
              control={control}
              name="dReviewCompletedDate"
              as={
                <DatePicker
                  label={applicationFieldLabels.dReviewCompletedDate}
                  required
                  dataTest="d-review-completed-date"
                  readOnly={!canEdit}
                />
              }
            />
          </Grid>
          <Grid item xs={12} xl={4} lg={6}>
            <Controller
              control={control}
              name="mainReviewCompletedDate"
              as={
                <DatePicker
                  label={applicationFieldLabels.mainReviewCompletedDate}
                  required
                  dataTest="main-review-completed-date"
                  readOnly={!canEdit}
                />
              }
            />
          </Grid>
          <Grid item xs={12} xl={6} lg={6}>
            <Controller
              control={control}
              name="reviewOutcome"
              as={
                <Select
                  label={applicationFieldLabels.reviewOutcome}
                  lookupTracker={lookupTracker}
                  datatest="review-outcome"
                  readOnly={!canEdit}
                >
                  {reviewOutcomeLookups}
                </Select>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <strong>Review Documents</strong>
            <ReviewDocuments
              control={control}
              fieldName="reviewAttachments"
              download={downloadReviewBlob}
              readOnly={!canEdit}
            />
          </Grid>
          <Grid item xs={12} xl={6} lg={6}>
            <Controller
              control={control}
              name="internalMeetingDate"
              as={
                <DatePicker
                  label={applicationFieldLabels.internalMeetingDate}
                  required
                  dataTest="application-review-date"
                  readOnly={!canEdit}
                />
              }
            />
          </Grid>
          <Grid item xs={12} xl={6} lg={6}>
            <Controller
              control={control}
              name="internalMeetingOutcome"
              as={
                <Select
                  label={applicationFieldLabels.internalMeetingOutcome}
                  required
                  datatest="application-review-outcome"
                  lookupTracker={lookupTracker}
                  readOnly={!canEdit}
                >
                  {reviewMeetingOutcomeLookups}
                </Select>
              }
            />
          </Grid>

          <Grid item xs={12} xl={6} lg={6}>
            <Controller
              control={control}
              name="panelMeetingDate"
              as={
                <DatePicker
                  label={applicationFieldLabels.panelMeetingDate}
                  required
                  dataTest="panel-review-date"
                  readOnly={!canEdit}
                />
              }
            />
          </Grid>
          <Grid item xs={12} xl={6} lg={6}>
            <Controller
              control={control}
              name="panelDecision"
              as={
                <Select
                  label={applicationFieldLabels.panelDecision}
                  required
                  datatest="panel-review-decision"
                  lookupTracker={lookupTracker}
                  readOnly={!canEdit}
                >
                  {panelDecisionLookups}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <strong>Panel Documents</strong>
            <ReviewDocuments
              control={control}
              fieldName="panelAttachments"
              download={downloadPanelBlob}
              readOnly={!canEdit}
            />
          </Grid>
          <Grid item xs={12} xl={6} lg={6}>
            <Controller
              control={control}
              name="dateOfDecisionLetter"
              as={
                <DatePicker
                  label={applicationFieldLabels.dateOfDecisionLetter}
                  required
                  dataTest="date-of-decision-letter"
                  readOnly={!canEdit}
                />
              }
            />
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6">Feedback</Typography>

      <Paper className={classes.formSection}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="feedbackMeetingRequested"
              render={(props) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) => {
                        props.onChange(e.target.checked);
                      }}
                      checked={props.value}
                      data-test="feedback-meeting-requested-check"
                      readOnly={!canEdit}
                    />
                  }
                  label={applicationFieldLabels.feedbackMeetingRequested}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} xl={6} lg={6}>
            <Controller
              control={control}
              name="feedbackMeetingDate"
              as={
                <DatePicker
                  label={applicationFieldLabels.feedbackMeetingDate}
                  required
                  dataTest="date-of-feedback-meeting"
                  readOnly={!canEdit}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="feedbackMeetingNotes"
              as={
                <TextField
                  label={applicationFieldLabels.feedbackMeetingNotes}
                  required
                  data-test="feedback-meeting-notes"
                  multiline
                  rows={4}
                  readOnly={!canEdit}
                />
              }
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};
